import type { ButtonProps } from '@chakra-ui/react';
import { Box, Button, Flex, HStack, PopoverTrigger } from '@chakra-ui/react';

import React from 'react';
import { useFilterButtonContext } from './useFilterButtonContext';
import { PlusCircle, XmarkCircle } from 'iconoir-react';

interface FilterButtonActionProps extends ButtonProps {
  children?: React.ReactNode; // To allow composing label/active options inside
}

export const FilterButtonAction: React.FC<FilterButtonActionProps> = ({
  children,
  ...buttonProps
}) => {
  const { isFilterActive, onClear } = useFilterButtonContext();

  const handleClearClick = (event: React.MouseEvent<HTMLSpanElement>) => {
    event.stopPropagation(); // Prevent the popover from opening/toggling
    onClear();
  };

  return (
    <PopoverTrigger asChild>
      <Button
        size="sm"
        variant="outline"
        colorPalette="gray"
        onClick={buttonProps.onClick}
        h="36px"
        pl="9px"
        pr="10px"
        py={0}
        rounded="10px"
        bg="white"
        // An active filter carries an ink outline, so the bar shows at a glance
        // which filters are narrowing the list.
        borderColor={isFilterActive ? 'primary.900' : 'hairline.control'}
        color="primary.900"
        fontFamily="heading"
        fontSize="12.5px"
        fontWeight="medium"
        transition="background-color .15s, border-color .15s"
        _hover={{
          bg: 'background',
          borderColor: isFilterActive ? 'primary.900' : 'hairline.controlHover',
        }}
        {...buttonProps}
      >
        <HStack gap={0.5} align="center">
          <Box
            display="inline-flex"
            color="inkMuted"
            cursor={isFilterActive ? 'pointer' : 'default'}
            onClick={isFilterActive ? handleClearClick : undefined}
            aria-label={isFilterActive ? 'Clear filter' : 'Add filter'}
            mr={1}
          >
            {isFilterActive ? (
              <XmarkCircle width={16} height={16} aria-hidden />
            ) : (
              <PlusCircle width={16} height={16} aria-hidden />
            )}
          </Box>

          <Flex alignItems="center" lineHeight="inherit">
            {children}
          </Flex>
        </HStack>
      </Button>
    </PopoverTrigger>
  );
};
